import * as React from 'react';

import { enumCustomAttributeEntityType } from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  MultiSelect,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $facilityGroupForm,
  $resetFacilityGroupForm,
  $updateFacilityGroupForm,
} from '../../_state/facilityGroupForm.state';
import {
  $createFacilityGroup,
  $isAddFacilityGroupDialogOpen,
  $rawLocationList,
} from '../../_state/locations.state';
import { FormSection } from '../_shared/FormSection';

import { CustomAttributeFieldsSection } from '~/shared/components/custom-attributes/CustomAttributesSection';
import { useCustomAttributeDefinitionsQuery } from '~/shared/graphql/queries/useCustomAttributeDefinitionsQuery';

export const LocationGroupCreationDialog = () => {
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom($isAddFacilityGroupDialogOpen);
  const form = useAtomValue($facilityGroupForm);
  const updateForm = useSetAtom($updateFacilityGroupForm);
  const resetForm = useSetAtom($resetFacilityGroupForm);
  const [{ isPending }, createFacilityGroup] = useAtom($createFacilityGroup);
  const locationList = useAtomValue($rawLocationList);

  const { data: customAttributeDefinitions } =
    useCustomAttributeDefinitionsQuery({
      entityType: enumCustomAttributeEntityType.CoreFacilityGroup,
    });

  const hasCustomFields = (customAttributeDefinitions?.length ?? 0) > 0;

  const [customAttributes, setCustomAttributes] = React.useState<
    Record<string, boolean | string | number | null | undefined>
  >({});

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
      setCustomAttributes({});
    }
  }, [isOpen, resetForm]);

  const facilityOptions = React.useMemo(
    () =>
      locationList.data?.map((facility) => ({
        key: facility.id,
        label: facility.name,
      })) ?? [],
    [locationList.data],
  );

  const defaultFacilityOptions = React.useMemo(
    () =>
      facilityOptions.filter((option) => form.facilityIds.includes(option.key)),
    [facilityOptions, form.facilityIds],
  );

  React.useEffect(() => {
    if (
      form.defaultFacilityId &&
      !form.facilityIds.includes(form.defaultFacilityId)
    ) {
      updateForm({ defaultFacilityId: undefined });
    }
  }, [form.facilityIds, form.defaultFacilityId, updateForm]);

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
    setCustomAttributes({});
  };

  const handleCreate = async () => {
    try {
      await createFacilityGroup({
        name: form.name,
        facilityIds: form.facilityIds.length > 0 ? form.facilityIds : undefined,
        defaultFacilityId: form.defaultFacilityId,
        customAttributes:
          Object.keys(customAttributes).length > 0
            ? customAttributes
            : undefined,
      });
      toast.success('Facility group created successfully');
      handleClose();
    } catch (error) {
      console.error('Failed to create facility group:', error);
      toast.error('Failed to create facility group');
    }
  };

  const canSubmit =
    form.name && form.facilityIds.length > 0 && !!form.defaultFacilityId;

  return (
    <ConsoleDialog
      className="@LocationGroupCreationDialog"
      title="Add Group"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={form.name !== '' || form.facilityIds.length > 0}
      content={
        <Col gap6>
          <FormSection title="Group Info">
            <Col gap4>
              <TextField
                label="Group Name"
                value={form.name}
                onChange={(value) => updateForm({ name: value })}
                isRequired
              />

              <MultiSelect
                label="Facilities"
                required
                options={facilityOptions}
                selectedKeys={form.facilityIds}
                onSelectionChange={(value) =>
                  updateForm({ facilityIds: value })
                }
                placeholder="Select facilities..."
                isLoading={locationList.isLoading}
              />

              <Select
                label="Default Facility"
                value={form.defaultFacilityId}
                onChange={(value) =>
                  updateForm({ defaultFacilityId: value || undefined })
                }
                options={defaultFacilityOptions}
                placeholder="Select..."
                isRequired
                isDisabled={form.facilityIds.length === 0}
              />
            </Col>
          </FormSection>

          {hasCustomFields && (
            <FormSection title="Custom Fields">
              <CustomAttributeFieldsSection
                entityType={enumCustomAttributeEntityType.CoreFacilityGroup}
                columns={2}
                values={customAttributes}
                onChange={setCustomAttributes}
              />
            </FormSection>
          )}
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleCreate}
        >
          Create Facility Group
        </Button>
      }
    />
  );
};
